/**
 * Conversion Tracking Utility
 * Centralized tracking for key conversion events with pluggable analytics adapters
 */

import * as Sentry from '@sentry/react';
import { supabase } from '@/integrations/supabase/client';
import type { Json } from '@/integrations/supabase/types';

export type ConversionEvent = 
  | 'homepage_view'
  | 'voice_demo_page_view'
  | 'preview_generated'
  | 'preview_to_trial_click'
  | 'trial_signup_started'
  | 'trial_signup_completed'
  | 'four_pillars_view'
  | 'contentdna_mention_view'
  | 'pricing_page_view'
  | 'features_page_view'
  | 'cost_comparison_view'
  | 'industry_stats_view'
  | 'solution_page_view'
  | 'platform_page_view';

interface ConversionEventData {
  event: ConversionEvent;
  timestamp: number;
  url: string;
  metadata?: Record<string, unknown>;
}

/**
 * Analytics Adapter Interface
 * Allows pluggable analytics providers (Sentry, Supabase, PostHog, Mixpanel, etc.)
 */
interface AnalyticsAdapter {
  track(event: string, properties?: Record<string, unknown>): void | Promise<void>;
  identify?(userId: string, traits?: Record<string, unknown>): void | Promise<void>;
  page?(name?: string, properties?: Record<string, unknown>): void | Promise<void>;
}

/**
 * Sentry Analytics Adapter
 * Tracks events as breadcrumbs for error context and debugging
 */
class SentryAnalyticsAdapter implements AnalyticsAdapter {
  track(event: string, properties?: Record<string, unknown>): void {
    Sentry.addBreadcrumb({
      category: 'analytics',
      message: event,
      level: 'info',
      data: properties,
    });
  }

  identify(userId: string, traits?: Record<string, unknown>): void {
    Sentry.setUser({ id: userId, ...traits });
  }

  page(name?: string, properties?: Record<string, unknown>): void {
    Sentry.addBreadcrumb({
      category: 'navigation',
      message: `Page: ${name || 'unknown'}`,
      level: 'info',
      data: properties,
    });
  }
}

/**
 * Supabase Analytics Adapter
 * Persists events to database for dashboard analytics and funnel analysis
 */
class SupabaseAnalyticsAdapter implements AnalyticsAdapter {
  async track(event: string, properties?: Record<string, unknown>): Promise<void> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const { data: userRole } = user 
        ? await supabase.from('user_roles').select('organization_id').eq('user_id', user.id).maybeSingle()
        : { data: null };

      await supabase.from('analytics_events').insert([{
        event_name: event,
        user_id: user?.id || null,
        organization_id: userRole?.organization_id || null,
        properties: (properties || {}) as Json,
        page_url: window.location.href,
        session_id: this.getSessionId(),
      }]);
    } catch (error) {
      // Silently fail to avoid disrupting user experience
      if (import.meta.env.DEV) {
        console.warn('[SupabaseAnalyticsAdapter] Failed to track event', error);
      }
    }
  }

  private getSessionId(): string {
    let sessionId = sessionStorage.getItem('analytics_session_id');
    if (!sessionId) {
      sessionId = crypto.randomUUID();
      sessionStorage.setItem('analytics_session_id', sessionId);
    }
    return sessionId;
  }
}

/**
 * Console Analytics Adapter (Development Only)
 * Logs events to console for debugging
 */
class ConsoleAnalyticsAdapter implements AnalyticsAdapter {
  track(event: string, properties?: Record<string, unknown>): void {
    console.log('[Analytics]', event, properties);
  }

  identify(userId: string, traits?: Record<string, unknown>): void {
    console.log('[Analytics] Identify', userId, traits);
  }

  page(name?: string, properties?: Record<string, unknown>): void {
    console.log('[Analytics] Page', name, properties);
  }
}

class ConversionTracker {
  private isInitialized = false;
  private adapters: AnalyticsAdapter[] = [];

  /**
   * Initialize tracking with configured adapters
   */
  initialize() {
    if (this.isInitialized) return;
    this.isInitialized = true;

    // Always include Sentry for error context
    this.adapters.push(new SentryAnalyticsAdapter());

    // Include Supabase adapter for database persistence
    this.adapters.push(new SupabaseAnalyticsAdapter());

    // Development-only console logging
    if (import.meta.env.DEV) {
      this.adapters.push(new ConsoleAnalyticsAdapter());
    }

    // Track initial page view
    this.trackPageView();
  }
    
  /**
   * Track a conversion event across all configured adapters
   */
  track(event: ConversionEvent, metadata?: Record<string, unknown>) {
    const data: ConversionEventData = {
      event,
      timestamp: Date.now(),
      url: window.location.href,
      metadata,
    };

    // Store in localStorage for offline resilience
    this.storeEvent(data);

    // Send to all analytics adapters
    this.adapters.forEach(adapter => {
      try {
        adapter.track(event, metadata);
      } catch (error) {
        if (import.meta.env.DEV) {
          console.error('[ConversionTracker] Adapter failed', adapter.constructor.name, error);
        }
      }
    });
  }

  /**
   * Identify user for analytics
   */
  identify(userId: string, traits?: Record<string, unknown>) {
    this.adapters.forEach(adapter => {
      try {
        adapter.identify?.(userId, traits);
      } catch (error) {
        if (import.meta.env.DEV) {
          console.error('[ConversionTracker] Identify failed', error);
        }
      }
    });
  }

  /**
   * Track page view based on current route
   */
  private trackPageView() {
    const path = window.location.pathname;
    
    if (path === '/') {
      this.track('homepage_view');
    } else if (path === '/voice-demo') {
      this.track('voice_demo_page_view');
    } else if (path === '/pricing' || path === '/plans') {
      this.track('pricing_page_view');
    } else if (path === '/features') {
      this.track('features_page_view');
    } else if (path.startsWith('/solutions/')) {
      this.track('solution_page_view', { page: path });
    } else if (path.startsWith('/platform/')) {
      this.track('platform_page_view', { page: path });
    }

    // Track page in adapters that support it
    this.adapters.forEach(adapter => {
      try {
        adapter.page?.(path);
      } catch (error) {
        if (import.meta.env.DEV) {
          console.error('[ConversionTracker] Page tracking failed', error);
        }
      }
    });
  }

  /**
   * Store event in localStorage for offline resilience
   */
  private storeEvent(data: ConversionEventData) {
    try {
      const key = 'gs_conversion_events';
      const stored = localStorage.getItem(key);
      const events: ConversionEventData[] = stored ? JSON.parse(stored) : [];
      
      // Keep only last 100 events
      events.push(data);
      if (events.length > 100) {
        events.shift();
      }

      localStorage.setItem(key, JSON.stringify(events));
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('[ConversionTracker] Failed to store event', error);
      }
    }
  }

  /**
   * Get all tracked events (for debugging)
   */
  getEvents(): ConversionEventData[] {
    try {
      const stored = localStorage.getItem('gs_conversion_events');
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  }

  /**
   * Clear all tracked events
   */
  clearEvents() {
    localStorage.removeItem('gs_conversion_events');
  }
}

// Singleton instance
export const conversionTracker = new ConversionTracker();
